import { action, makeObservable, observable, runInAction } from 'mobx';

import { NpvrEventItem, NpvrStatusInfo } from '@smart-tv/typings/npvr';
import getNpvrStatus from '@smart-tv/utils/kmm/getNpvrStatus';
import getRecordedNpvrEvents from '@smart-tv/utils/kmm/getRecordedNpvrEvents';
import getScheduledNpvrEvents from '@smart-tv/utils/kmm/getScheduledNpvrEvents';
import getExpiringSoonNpvrEvents from '@smart-tv/utils/kmm/getExpiringSoonNpvrEvents';
import getFailedNpvrEvents from '@smart-tv/utils/kmm/getFailedNpvrEvents';
import { rootStore as stripesRootStore } from '@smart-tv/components/stripes/_stores/store-root';
import { RECORDINGS_STRIPE_ID } from '@smart-tv/components/stripes/_utils/types';

// Short safety-net TTL - none of the NPVR tabs are server-pushed, so data can also drift on its
// own (e.g. a scheduled recording completing). The main win here is skipping a refetch when
// flipping between tabs or reopening the dialog within this window, not long-term caching.
const CACHE_TTL_MS = 60_000;

export interface Resource<T> {
  data: T | undefined;
  loading: boolean;
  error: unknown;
  fetchedAt: number;
}

const createResource = <T>(): Resource<T> => ({
  data: undefined,
  loading: false,
  error: null,
  fetchedAt: 0
});

export enum NpvrResourceKey {
  Status = 'status',
  Recordings = 'recordings',
  Scheduled = 'scheduled',
  ExpiringSoon = 'expiringSoon',
  Failed = 'failed'
}

type EventResourceKey = Exclude<NpvrResourceKey, NpvrResourceKey.Status>;

export class NpvrStore {
  status: Resource<NpvrStatusInfo> = createResource();
  recordings: Resource<NpvrEventItem[]> = createResource();
  scheduled: Resource<NpvrEventItem[]> = createResource();
  expiringSoon: Resource<NpvrEventItem[]> = createResource();
  failed: Resource<NpvrEventItem[]> = createResource();

  // In-flight requests, keyed by resource and tagged with the resource's generation at the
  // time they started. Lets concurrent callers for the *same* generation join one request,
  // while a response tied to an older generation (invalidated/mutated mid-flight) is neither
  // joined by new callers nor allowed to write its data once it resolves - see generations
  // below.
  private pendingStatusFetch: { generation: number; promise: Promise<NpvrStatusInfo | undefined> } | null =
    null;
  private pendingEventFetches: Partial<
    Record<EventResourceKey, { generation: number; promise: Promise<NpvrEventItem[] | undefined> }>
  > = {};

  // Bumped per-resource by invalidate()/removeRecording()/removeScheduled()/reset(). A fetch
  // captures its resource's generation before the network call; if that generation has moved
  // on by the time the response arrives (e.g. the item was deleted while the request was still
  // in flight), the response is discarded instead of resurrecting stale data.
  private generations: Record<NpvrResourceKey, number> = {
    [NpvrResourceKey.Status]: 0,
    [NpvrResourceKey.Recordings]: 0,
    [NpvrResourceKey.Scheduled]: 0,
    [NpvrResourceKey.ExpiringSoon]: 0,
    [NpvrResourceKey.Failed]: 0
  };

  constructor() {
    makeObservable(this, {
      status: observable,
      recordings: observable,
      scheduled: observable,
      expiringSoon: observable,
      failed: observable,
      setStatus: action,
      setEvents: action,
      setLoading: action,
      removeRecording: action,
      removeScheduled: action,
      invalidate: action,
      reset: action
    });
  }

  private isFresh(resource: Resource<unknown>, force: boolean): boolean {
    return !force && resource.data !== undefined && Date.now() - resource.fetchedAt < CACHE_TTL_MS;
  }

  setLoading = (key: NpvrResourceKey) => {
    switch (key) {
      case NpvrResourceKey.Status:
        this.status = { ...this.status, loading: true };
        return;
      case NpvrResourceKey.Recordings:
        this.recordings = { ...this.recordings, loading: true };
        return;
      case NpvrResourceKey.Scheduled:
        this.scheduled = { ...this.scheduled, loading: true };
        return;
      case NpvrResourceKey.ExpiringSoon:
        this.expiringSoon = { ...this.expiringSoon, loading: true };
        return;
      case NpvrResourceKey.Failed:
        this.failed = { ...this.failed, loading: true };
        return;
    }
  };

  setStatus = (data: NpvrStatusInfo) => {
    this.status = { data, loading: false, error: null, fetchedAt: Date.now() };
  };

  setEvents = (key: EventResourceKey, data: NpvrEventItem[]) => {
    this[key] = { data, loading: false, error: null, fetchedAt: Date.now() };
  };

  fetchStatus(force = false): Promise<NpvrStatusInfo | undefined> {
    if (this.isFresh(this.status, force)) return Promise.resolve(this.status.data);

    const generation = this.generations[NpvrResourceKey.Status];
    const pending = this.pendingStatusFetch;
    if (pending && pending.generation === generation) return pending.promise;

    this.setLoading(NpvrResourceKey.Status);

    const promise = getNpvrStatus()
      .then((data) => {
        if (this.generations[NpvrResourceKey.Status] !== generation) return this.status.data;
        runInAction(() => this.setStatus(data));
        return data;
      })
      .catch((error) => {
        if (this.generations[NpvrResourceKey.Status] === generation) {
          runInAction(() => {
            this.status = { ...this.status, loading: false, error };
          });
        }
        throw error;
      })
      .finally(() => {
        if (this.pendingStatusFetch?.generation === generation) this.pendingStatusFetch = null;
      });

    this.pendingStatusFetch = { generation, promise };
    return promise;
  }

  private fetchEvents(
    key: EventResourceKey,
    fetcher: () => Promise<NpvrEventItem[]>,
    force: boolean
  ): Promise<NpvrEventItem[] | undefined> {
    if (this.isFresh(this[key], force)) return Promise.resolve(this[key].data);

    const generation = this.generations[key];
    const pending = this.pendingEventFetches[key];
    if (pending && pending.generation === generation) return pending.promise;

    this.setLoading(key);

    const promise = fetcher()
      .then((data) => {
        if (this.generations[key] !== generation) return this[key].data;
        runInAction(() => this.setEvents(key, data));
        return data;
      })
      .catch((error) => {
        if (this.generations[key] === generation) {
          runInAction(() => {
            this[key] = { ...this[key], loading: false, error };
          });
        }
        throw error;
      })
      .finally(() => {
        if (this.pendingEventFetches[key]?.generation === generation) {
          delete this.pendingEventFetches[key];
        }
      });

    this.pendingEventFetches[key] = { generation, promise };
    return promise;
  }

  fetchRecordings = (force = false) =>
    this.fetchEvents(NpvrResourceKey.Recordings, getRecordedNpvrEvents, force);
  fetchScheduled = (force = false) =>
    this.fetchEvents(NpvrResourceKey.Scheduled, getScheduledNpvrEvents, force);
  fetchExpiringSoon = (force = false) =>
    this.fetchEvents(NpvrResourceKey.ExpiringSoon, getExpiringSoonNpvrEvents, force);
  fetchFailed = (force = false) => this.fetchEvents(NpvrResourceKey.Failed, getFailedNpvrEvents, force);

  // Optimistic cache update (not just invalidation) for a single deleted recording - the
  // store is the source of truth consumers render from, so this needs to update that data
  // directly rather than relying on a future refetch. expiringSoon entries are themselves
  // recorded events (same eventId space), so a deleted recording must be removed from both -
  // this is the single shared helper every deletion path should call.
  removeRecording = (eventId: number) => {
    if (this.recordings.data) {
      this.recordings = {
        ...this.recordings,
        data: this.recordings.data.filter((event) => event.eventId !== eventId)
      };
    }
    if (this.expiringSoon.data) {
      this.expiringSoon = {
        ...this.expiringSoon,
        data: this.expiringSoon.data.filter((event) => event.eventId !== eventId)
      };
    }
    // A response already in flight for the old (pre-deletion) state must not be allowed to
    // resurrect this item once it resolves.
    this.generations.recordings += 1;
    this.generations.expiringSoon += 1;
    stripesRootStore.stripesStore.addStripeToRefetch(RECORDINGS_STRIPE_ID);
  };

  // Same as removeRecording, but for cancelled scheduled/planned recordings - matcher lets
  // callers cancel either a single event or a whole series.
  removeScheduled = (matcher: (event: NpvrEventItem) => boolean) => {
    if (!this.scheduled.data) return;
    this.scheduled = {
      ...this.scheduled,
      data: this.scheduled.data.filter((event) => !matcher(event))
    };
    this.generations.scheduled += 1;
    stripesRootStore.stripesStore.addStripeToRefetch(RECORDINGS_STRIPE_ID);
  };

  // Call after any NPVR interaction that isn't covered by an optimistic update above (e.g.
  // storage usage, which we can't compute client-side), passing only the resources it
  // actually affects. Only resets fetchedAt (not data) - a resource currently being rendered
  // by an observer must keep showing its existing data instead of flashing to empty/loading,
  // it'll pick up fresh data next time it's fetched (stale-while-revalidate). Also bumps the
  // resource's generation, so a response already in flight from before the invalidating
  // interaction can't commit stale data once it resolves, and a subsequent fetch (even one
  // that joins an in-flight promise elsewhere) is guaranteed to be for current data. Also
  // marks the homepage Recordings stripe for refetch (same mechanism as use-npvr-sheet.ts),
  // which the hook only acts on while that stripe is mounted/visible - i.e. only "if the
  // page is active".
  invalidate = (keys: NpvrResourceKey[]) => {
    keys.forEach((key) => {
      this.generations[key] += 1;
      switch (key) {
        case NpvrResourceKey.Status:
          this.status = { ...this.status, fetchedAt: 0 };
          break;
        case NpvrResourceKey.Recordings:
          this.recordings = { ...this.recordings, fetchedAt: 0 };
          break;
        case NpvrResourceKey.Scheduled:
          this.scheduled = { ...this.scheduled, fetchedAt: 0 };
          break;
        case NpvrResourceKey.ExpiringSoon:
          this.expiringSoon = { ...this.expiringSoon, fetchedAt: 0 };
          break;
        case NpvrResourceKey.Failed:
          this.failed = { ...this.failed, fetchedAt: 0 };
          break;
      }
    });
    stripesRootStore.stripesStore.addStripeToRefetch(RECORDINGS_STRIPE_ID);
  };

  // Call on logout/account switch - this is a module-level singleton, so it otherwise
  // survives across sessions and would render the previous account's recordings/storage
  // usage. Bumping every generation also makes any still in-flight request from the old
  // session a no-op once it resolves, instead of repopulating the new session's cache.
  reset = () => {
    (Object.keys(this.generations) as NpvrResourceKey[]).forEach((key) => {
      this.generations[key] += 1;
    });
    this.pendingStatusFetch = null;
    this.pendingEventFetches = {};
    this.status = createResource();
    this.recordings = createResource();
    this.scheduled = createResource();
    this.expiringSoon = createResource();
    this.failed = createResource();
  };
}

export default NpvrStore;

